<script setup lang="ts">
/**
 * Конструктор комбо: две колонки на десктопе (медиа/экран выбора слева, слоты справа),
 * одна колонка на мобильном. Экраны «Заменить продукт» и «Меняйте на свой вкус»
 * открываются поверх медиа-колонки (десктоп) или на весь экран (мобильный).
 */

import { computed, ref, watch } from 'vue';
import { consola } from 'consola';
import ComboSlotCard from './ComboSlotCard.vue';
import ComboReplacePicker from './ComboReplacePicker.vue';
import ComboCustomizePanel from './ComboCustomizePanel.vue';
import { useCartStore } from '@entities/order';
import { useInstitutionStore } from '@entities/institution';
import { ComboScene, comboSceneLayers } from '@entities/combo';
import type { ComboGroupItem, ComboProduct } from '@entities/product';
import { menuUnavailableLabel, menuUnavailableHint } from '@entities/product';
import { useComboBuilder, useComboCart } from '@features/combo';
import { useKitToast } from '@shared/ui/kit';
import ProductMediaSlider from '~/entities/product/ui/ProductMediaSlider.vue';
import ProductActions from '~/entities/product/ui/ProductActions.vue';

const props = defineProps<{
  combo: ComboProduct | null;
  branchId: string | null;
}>();

const emit = defineEmits<{
  (e: 'openBasket'): void;
}>();

const logger = consola.withTag('ComboBuilder');
const cartStore = useCartStore();
const institutionStore = useInstitutionStore();

const placeholderImg = computed(
  () => (institutionStore.institution as any)?.visual?.placeholder_file,
);

const comboRef = computed(() => props.combo);
const branchIdRef = computed(() => props.branchId);

const { toast } = useKitToast();

const {
  slots,
  calculatedPrice,
  isValid,
  blockedGroup,
  cartSelections,
  getPriceDelta,
  selectProduct,
  setSlotAddons,
  setSlotExcludes,
  resetSlotCustomization,
  getCartData,
} = useComboBuilder({ combo: comboRef, branchId: branchIdRef });

const {
  comboInCart,
  comboQuantity,
  handleComboQuantityChange,
  isLoading: isComboLoading,
} = useComboCart({ combo: comboRef, branchId: branchIdRef, selections: cartSelections });

// ─── Экраны поверх медиа ─────────────────────────────────────────────────────
type OverlayMode = 'replace' | 'customize';
const overlay = ref<{ mode: OverlayMode; groupId: number } | null>(null);

const activeSlot = computed(() =>
  overlay.value ? slots.value.find((s) => s.group.id === overlay.value!.groupId) ?? null : null,
);

const openReplace = (groupId: number) => {
  overlay.value = { mode: 'replace', groupId };
};

const openCustomize = (groupId: number) => {
  overlay.value = { mode: 'customize', groupId };
};

const closeOverlay = () => {
  overlay.value = null;
};

/** Доплата за товар относительно текущего выбора в активном слоте */
const activeSlotPriceDelta = (product: ComboGroupItem): number =>
  activeSlot.value ? getPriceDelta(activeSlot.value.group, product) : 0;

/** Замена товара не закрывает экран: пользователь может перебирать варианты */
const handleReplaceSelect = (groupId: number, productId: number) => {
  selectProduct(groupId, productId);
};

/**
 * Гость ткнул в карточку с красной рамкой. Молча игнорировать клик нельзя — это читается
 * как поломка сайта, поэтому объясняем причину тостом.
 */
const handleUnavailableSelect = (product: ComboGroupItem) => {
  toast({
    color: 'warning',
    title: `«${product.name}» — ${menuUnavailableLabel(product).toLowerCase()}`,
    description: menuUnavailableHint(product),
    timeout: 5000,
  });
};

// ─── Корзина ─────────────────────────────────────────────────────────────────
const isAdding = ref(false);
const isLoading = computed(() => isAdding.value || isComboLoading.value);

const addToCart = async () => {
  if (!isValid.value || isLoading.value) return;

  isAdding.value = true;
  try {
    const data = getCartData();
    if (data) await cartStore.addComboToCart(data);
  } catch (error: any) {
    logger.error('Ошибка добавления комбо в корзину', error);

    // Состав мог протухнуть, пока гость собирал комбо: причину сервер пишет
    // по-человечески («Кола сейчас в стоп-листе») — показываем её как есть.
    const status = error?.status ?? error?.statusCode ?? error?.response?.status;
    const serverMessage: string | undefined =
      error?.response?._data?.message ?? error?.data?.message;

    if (status === 400 && serverMessage) {
      toast({
        color: 'warning',
        title: 'Комбо не добавлено',
        description: serverMessage,
        timeout: 6000,
      });
    }
  } finally {
    isAdding.value = false;
  }
};

/**
 * Почему кнопка заблокирована. «Выберите все обязательные опции» врёт, когда выбирать
 * нечего: в группе не осталось ни одного доступного товара.
 */
const disabledMessage = computed(() => {
  if (isValid.value) return '';
  if (blockedGroup.value) {
    return `Комбо сейчас недоступно: в группе «${blockedGroup.value.name}» закончились все варианты`;
  }
  return 'Выберите все обязательные опции';
});

// ─── Цена ────────────────────────────────────────────────────────────────────
const hasDiscount = computed(() => calculatedPrice.value.discountAmount > 0);
const oldPrice = computed(() => Math.round(calculatedPrice.value.basePrice));
const finalPrice = computed(() => Math.round(calculatedPrice.value.finalPrice));

// Составная картинка: позиции, выбранные в слотах, на своих местах.
// Считаем от slots, а не от combo.groups: в слоте лежит уже выбранная позиция,
// поэтому сцена пересобирается ровно тогда, когда гость меняет товар.
const sceneLayers = computed(() =>
  comboSceneLayers(
    slots.value.map((comboSlot) => ({
      id: comboSlot.group.id,
      layout: comboSlot.group.layout,
      products: comboSlot.product ? [comboSlot.product] : [],
    })),
  ),
);

// Состав слотов подгружает сама модель (useComboBuilder следит за комбо и филиалом).
// Здесь только закрываем экран выбора, если пользователь открыл другое комбо.
watch(() => props.combo?.id, closeOverlay);
</script>

<template>
  <div class="combo-builder" data-testid="combo-builder">
    <div class="combo-builder__grid">
      <!-- Левая колонка: медиа комбо.
           Слоты расставлены — собираем картинку из выбранных позиций: гость видит именно
           тот набор, который собрал. Нет раскладки — обычный слайдер картинок комбо. -->
      <div class="combo-builder__media">
        <ComboScene
          v-if="sceneLayers.length > 0"
          :layers="sceneLayers"
          shadows
          class="combo-builder__scene"
        />
        <ProductMediaSlider
          v-else
          :current-variant="(combo as any)"
          :placeholder-img="placeholderImg"
        />
      </div>

      <!-- Правая колонка: слоты и цена -->
      <div class="combo-builder__panel">
        <div class="combo-builder__scroll">
          <h2 class="combo-builder__title">{{ combo?.name }}</h2>
          <p v-if="combo?.description" class="combo-builder__description">
            {{ combo.description }}
          </p>

          <div class="combo-builder__slots">
            <ComboSlotCard
              v-for="comboSlot in slots"
              :key="comboSlot.group.id"
              :combo-slot="comboSlot"
              :active="overlay?.groupId === comboSlot.group.id"
              :dimmed="!!overlay && overlay.groupId !== comboSlot.group.id"
              :placeholder-img="placeholderImg"
              @replace="openReplace(comboSlot.group.id)"
              @customize="openCustomize(comboSlot.group.id)"
              @select-variant="selectProduct(comboSlot.group.id, $event)"
            />
          </div>
        </div>

        <div class="combo-builder__footer">
          <!--
            Причину блокировки показываем здесь: ProductActions принимает disabledMessage,
            но нигде его не рисует, и гость видел серую кнопку без единого слова о том,
            почему комбо не собрать.
          -->
          <p v-if="disabledMessage" class="combo-builder__disabled-message">
            {{ disabledMessage }}
          </p>
          <ProductActions
            :in-cart="comboInCart"
            :quantity="comboQuantity"
            :total-price="finalPrice"
            :old-price="hasDiscount ? oldPrice : null"
            :is-loading="isLoading"
            :is-disabled="!isValid"
            :disabled-message="disabledMessage"
            @add-to-cart="addToCart"
            @quantity-change="handleComboQuantityChange"
            @open-basket="emit('openBasket')"
          />
        </div>
      </div>
    </div>

    <!-- Экраны выбора: поверх медиа на десктопе, на весь экран на мобильном -->
    <Transition name="combo-overlay">
      <div v-if="activeSlot" class="combo-builder__overlay">
        <ComboReplacePicker
          v-if="overlay?.mode === 'replace'"
          :combo-slot="activeSlot"
          :placeholder-img="placeholderImg"
          :price-delta="activeSlotPriceDelta"
          @select="handleReplaceSelect(activeSlot.group.id, $event)"
          @select-unavailable="handleUnavailableSelect"
          @close="closeOverlay"
        />
        <ComboCustomizePanel
          v-else
          :combo-slot="activeSlot"
          :placeholder-img="placeholderImg"
          @update-addons="setSlotAddons(activeSlot.group.id, $event)"
          @update-excludes="setSlotExcludes(activeSlot.group.id, $event)"
          @reset="resetSlotCustomization(activeSlot.group.id)"
          @close="closeOverlay"
        />
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.combo-builder {
  @apply relative h-full min-w-0;

  &__grid {
    @apply dark:bg-zinc-900;
    background-color: #1a1a1a;
    @apply grid lg:grid-cols-[662px_auto] gap-2 h-full;
    @apply max-lg:h-[calc(100dvh-var(--safe-area-inset-top))] max-lg:grid-rows-[auto_1fr];
    @apply min-w-0 overflow-hidden;

    // Слотов может быть много: без потолка модалка вырастает выше экрана
    // и кнопка «В корзину» уезжает за нижний край.
    // minmax(0, 1fr) обязателен: иначе строка грида берёт высоту контента,
    // max-height только обрезает её, и кнопка просто исчезает под краем.
    @screen lg {
      max-height: calc(100dvh - 4rem);
      grid-template-rows: minmax(0, 1fr);
    }
  }

  &__media {
    @apply min-w-0 overflow-hidden;
  }

  // Сцене нужна своя высота: слои позиционируются в процентах от неё,
  // а сама она — не картинка и от контента не растягивается.
  &__scene {
    @apply w-full;
    aspect-ratio: 1 / 1;

    @screen lg {
      @apply h-full;
      aspect-ratio: auto;
    }
  }

  &__panel {
    @apply relative flex flex-col min-h-0 overflow-hidden;
    @apply pt-5 pb-4 px-5 lg:py-8 lg:px-8 text-start;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 0;
      pointer-events: none;
      border-radius: inherit;
      background-color: rgba(255, 255, 255, 0.55);
      backdrop-filter: blur(32px) saturate(1.6) brightness(1.05);
      -webkit-backdrop-filter: blur(32px) saturate(1.6) brightness(1.05);
    }

    .dark &::before {
      background-color: rgba(18, 18, 20, 0.68);
      backdrop-filter: blur(32px) saturate(1.4) brightness(0.9);
      -webkit-backdrop-filter: blur(32px) saturate(1.4) brightness(0.9);
    }

    & > * {
      position: relative;
      z-index: 1;
    }

    @media (max-width: 1023px) {
      border-top-left-radius: 1rem;
      border-top-right-radius: 1rem;
    }
  }

  &__scroll {
    @apply flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1;

    scrollbar-width: thin;
    scrollbar-color: rgba(156, 163, 175, 0.4) transparent;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(156, 163, 175, 0.4);
      border-radius: 2px;
    }
  }

  &__title {
    @apply text-xl lg:text-2xl font-bold text-gray-900 dark:text-white;
  }

  &__description {
    @apply text-sm text-gray-600 dark:text-gray-400 mt-1;
  }

  &__slots {
    @apply flex flex-col gap-3 mt-4;
  }

  &__footer {
    @apply shrink-0 pt-3;
  }

  &__disabled-message {
    @apply mb-2 text-center text-sm text-red-500 dark:text-red-400;
  }

  &__overlay {
    @apply absolute inset-0 z-20 overflow-hidden;
    @apply lg:right-auto lg:w-[662px] lg:rounded-none;
  }
}

.combo-overlay-enter-active,
.combo-overlay-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.combo-overlay-enter-from,
.combo-overlay-leave-to {
  opacity: 0;
  transform: translate3d(0, 1.5rem, 0);
}

@media (prefers-reduced-motion: reduce) {
  .combo-overlay-enter-active,
  .combo-overlay-leave-active {
    transition: opacity 0.2s ease;
  }

  .combo-overlay-enter-from,
  .combo-overlay-leave-to {
    transform: none;
  }
}
</style>
